import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Users, Database, History, Shield, Activity, CheckCircle, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/KPICard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { BINDA_LIST } from '../data/binda';
import { useStore } from '../data/store';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
import { SyncButton } from '../components/SyncButton';
export default function AdminBindaDashboard() {
  const navigate = useNavigate();
  const personnelList = useStore(s => s.personnel);
  const usersList = useStore(s => s.users);
  // Simulate getting the region for the current Admin BINDA.
  const regionData = BINDA_LIST.find(b => b.provinsi === 'Jawa Barat')!;
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filterFields: FilterField[] = [{
    key: 'date',
    label: 'Periode Waktu',
    type: 'datePreset'
  }, {
    key: 'status',
    label: 'Status Personel',
    type: 'statusBadge',
    options: [{
      label: 'Aktif',
      value: 'Aktif'
    }, {
      label: 'Luring',
      value: 'Luring'
    }]
  }];
  const regionalPersonnel = personnelList.filter(p => p.region === regionData.provinsi);
  const regionalUsers = usersList.filter(u => u.role === 'Admin BINDA' || u.role === 'Kepala BINDA');
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-531">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-532">
        <div data-id="element-533">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground uppercase" data-id="element-534">
            KONSOL ADMIN: {regionData.provinsi}
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-535">
            Manajemen Operasional & Sistem Regional
          </p>
        </div>
        <div className="flex gap-3 items-center" data-id="element-536">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-537">
            <Filter className="w-4 h-4 mr-2" data-id="element-538" />
            Filter
          </Button>
          <SyncButton data-id="element-539" />
          <div className="glass-panel px-3 py-1.5 rounded flex items-center gap-2 border-primary/30" data-id="element-540">
            <span className="relative flex h-2 w-2" data-id="element-541">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" data-id="element-542"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" data-id="element-543"></span>
            </span>
            <span className="text-xs font-mono text-primary tracking-widest" data-id="element-544">
              SISTEM OPTIMAL
            </span>
          </div>
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} data-id="element-545" />}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-546">
        <KPICard title="Personel Regional" value={regionalPersonnel.length} icon={Users} colorVar="var(--primary)" onClick={() => navigate('/personnel')} data-id="element-547" />
        <KPICard title="Pengguna Sistem" value={regionalUsers.length} icon={Shield} colorVar="var(--status-info)" onClick={() => navigate('/users')} data-id="element-548" />
        <KPICard title="Status Sinkronisasi" value="100%" icon={Database} colorVar="var(--status-active)" progress={{
        value: 100,
        label: 'Data Master',
        target: 'Sinkron'
      }} data-id="element-549" />
        <KPICard title="Log Audit (24j)" value="128" icon={History} colorVar="var(--muted-foreground)" onClick={() => navigate('/audit-log')} data-id="element-550" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-551">
        {/* Personnel Health */}
        <Card className="glass-panel border-border/50 flex flex-col" data-id="element-552">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-553">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-554">
              <Activity className="w-4 h-4 text-primary" data-id="element-555" /> Status Personel
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/personnel')} data-id="element-556">
              Kelola
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-557">
            <Table data-id="element-558">
              <TableHeader className="bg-black/20" data-id="element-559">
                <TableRow className="border-border/30 hover:bg-transparent" data-id="element-560">
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-561">
                    ID
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-562">
                    Nama
                  </TableHead>
                  <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-563">
                    Status
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-564">
                {regionalPersonnel.map(p => <TableRow key={p.id} className="border-border/20 hover:bg-white/5 transition-colors cursor-pointer" onClick={() => navigate(`/personnel/${p.id}`)} data-id="element-565">
                    <TableCell className="font-mono text-xs text-primary" data-id="element-566">
                      {p.id}
                    </TableCell>
                    <TableCell className="font-medium text-sm" data-id="element-567">
                      {p.name}
                    </TableCell>
                    <TableCell data-id="element-568">
                      <StatusBadge status={p.status === 'Aktif' ? 'Normal' : 'Offline'} data-id="element-569" />
                    </TableCell>
                  </TableRow>)}
                {regionalPersonnel.length === 0 && <TableRow data-id="element-570">
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground font-mono text-xs" data-id="element-571">
                      Tidak ada data personel.
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* System Alerts/Audit Highlights */}
        <Card className="glass-panel border-border/50 flex flex-col" data-id="element-572">
          <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-573">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-574">
              <AlertTriangle className="w-4 h-4 text-[var(--status-standby)]" data-id="element-575" />{' '}
              Peringatan Sistem
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-[10px] font-mono" onClick={() => navigate('/audit-log')} data-id="element-576">
              Log Lengkap
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-auto" data-id="element-577">
            <div className="divide-y divide-border/50" data-id="element-578">
              <div className="p-4 hover:bg-secondary/30 transition-colors" data-id="element-579">
                <div className="flex items-center gap-2 mb-1" data-id="element-580">
                  <AlertTriangle className="w-3 h-3 text-[var(--status-standby)]" data-id="element-581" />
                  <span className="font-mono text-xs font-bold" data-id="element-582">
                    Upaya Login Gagal
                  </span>
                </div>
                <p className="text-[10px] font-mono text-muted-foreground mb-1" data-id="element-583">
                  3 upaya gagal terdeteksi dari IP 192.168.1.105 untuk akun
                  admin.jabar.
                </p>
                <span className="text-[9px] font-mono text-muted-foreground" data-id="element-584">
                  10 menit yang lalu
                </span>
              </div>
              <div className="p-4 hover:bg-secondary/30 transition-colors" data-id="element-585">
                <div className="flex items-center gap-2 mb-1" data-id="element-586">
                  <CheckCircle className="w-3 h-3 text-[var(--status-active)]" data-id="element-587" />
                  <span className="font-mono text-xs font-bold" data-id="element-588">
                    Pembaruan Data Master
                  </span>
                </div>
                <p className="text-[10px] font-mono text-muted-foreground mb-1" data-id="element-589">
                  Sinkronisasi data wilayah berhasil diselesaikan.
                </p>
                <span className="text-[9px] font-mono text-muted-foreground" data-id="element-590">
                  1 jam yang lalu
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}